import React, { useState, useEffect } from 'react';
import { adminManagementService } from '../../services/adminService';
import {
    Users, Layers, CreditCard, Activity, Package, CheckSquare,
    Target, PlayCircle, Plug, Building2, Clock, Calendar as ScheduleIcon,
    RefreshCw
} from 'lucide-react';
import AdminStatCard from '../../components/admin/AdminStatCard';
import DateRangeFilter from '../../components/admin/DateRangeFilter';
import './AdminPages.css';

function AdminDashboard() {
    const [range, setRange] = useState('30d');
    const [customDates, setCustomDates] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadStats = async () => {
        try {
            setLoading(true);
            const params = { range };
            if (range === 'custom') {
                params.startDate = customDates.start;
                params.endDate = customDates.end;
            }
            const data = await adminManagementService.getStats(params);
            setStats(data);
        } catch (error) {
            console.error('Failed to load stats', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, [range, customDates.start, customDates.end]);

    const statCards = stats ? [
        { key: 'users', label: 'Total Users', icon: Users, color: '#38bdf8' },
        { key: 'workspaces', label: 'Total Workspaces', icon: Layers, color: '#a855f7' },
        { key: 'subscriptions', label: 'Active Subscriptions', icon: CreditCard, color: '#10b981' },
        { key: 'executionTime', label: 'Execution Time', icon: Clock, color: '#f43f5e', formatValue: (v) => `${v} hrs` },
        { key: 'testExecutions', label: 'Test Executions', icon: PlayCircle, color: '#06b6d4' },
        { key: 'goals', label: 'Test Goals', icon: Target, color: '#f97316' },
        { key: 'schedules', label: 'Schedules', icon: ScheduleIcon, color: '#eab308' },
        { key: 'apps', label: 'Parent Apps', icon: Package, color: '#f97316' },
        { key: 'executionPlans', label: 'Execution Plans', icon: CheckSquare, color: '#ef4444' },
        { key: 'integrations', label: 'Active Integrations', icon: Plug, color: '#8b5cf6' },
        { key: 'enterpriseLeads', label: 'Enterprise Leads', icon: Building2, color: '#14b8a6' },
    ] : [];

    return (
        <div className="admin-dashboard">
            <div className="admin-dashboard-header">
                <div>
                    <h1>System Overview</h1>
                    <p className="subtitle">Real-time platform metrics and usage trends</p>
                </div>
                <div className="admin-header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button 
                        className="admin-cancel-btn" 
                        onClick={loadStats}
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} style={{ marginRight: '8px' }} />
                        <span>Refresh</span>
                    </button>
                    <DateRangeFilter
                        range={range}
                        setRange={setRange}
                        customDates={customDates}
                        setCustomDates={setCustomDates}
                    />
                </div>
            </div>

            {!stats && !loading ? (
                <div className="admin-loading" style={{ color: '#ef4444' }}>
                    Failed to load system statistics. Please try refreshing.
                </div>
            ) : loading && !stats ? (
                <div className="admin-loading">Initializing dashboard...</div>
            ) : (
                <div className={`admin-dashboard-stats ${loading ? 'stats-refreshing' : ''}`}>
                    {statCards.map((card) => (
                        <AdminStatCard
                            key={card.key}
                            label={card.label}
                            {...stats[card.key]}
                            icon={card.icon}
                            color={card.color}
                            formatValue={card.formatValue}
                        />
                    ))}
                </div>
            )}

            <div className="admin-dashboard-footer">
                <div className="status-overview-card">
                    <div className="card-header">
                        <Activity size={18} />
                        <h3>Infrastructure Status</h3>
                    </div>
                    <div className="status-grid">
                        <div className="status-item">
                            <span className="dot online"></span>
                            <span>API Gateway: <strong>Operational</strong></span>
                        </div>
                        <div className="status-item">
                            <span className="dot online"></span>
                            <span>Worker Queue: <strong>Active</strong></span>
                        </div>
                        <div className="status-item">
                            <span className="dot online"></span>
                            <span>Device Farm: <strong>Connected</strong></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminDashboard;
